import get from 'lodash/get';
import { GenericObject } from './types';

export const getReactDependenciesFromPreferences = (
  preferences: GenericObject,
  id: string
) => {
  if (preferences.pageSettings) {
    const componentSettings = get(
      preferences.pageSettings,
      `pages.${preferences.pageSettings.currentPage}.componentSettings`,
      {}
    );
    return Object.keys(componentSettings).filter(i => i !== id);
  }
  const react = [];
  const searchId = get(
    preferences,
    'searchSettings.rsConfig.componentId',
    'search'
  );
  react.push(searchId);
  const staticFacets = get(preferences, 'facetSettings.staticFacets');
  if (staticFacets && Array.isArray(staticFacets)) {
    staticFacets.forEach(facet => {
      const componentId = get(staticFacets[facet], 'rsConfig.componentId');
      if (componentId && componentId !== id) {
        react.push(componentId);
      } else if (facet.name && facet.name !== id) {
        react.push(facet.name);
      }
    });
  }
  const dynamicFacets = get(preferences, 'facetSettings.dynamicFacets');
  if (dynamicFacets && Array.isArray(dynamicFacets)) {
    dynamicFacets.forEach(facet => {
      const componentId = get(facet, 'rsConfig.componentId');
      if (componentId && componentId !== id) {
        react.push(componentId);
      }
    });
  }
  return react;
};

const commonElementsInArray = (arr1: any[], arr2: any[]) => {
  return arr1.some(r => arr2.includes(r));
};

export const getDataFieldValue = (value: any) => {
  if (value && typeof value === 'object') {
    return value.dataField;
  }
  return value;
};

const getMetaDataFields = (meta: any): GenericObject => {
  const newMeta: GenericObject = {};
  if (Array.isArray(meta || [])) {
    (meta || []).forEach((obj: GenericObject) => {
      newMeta[obj.label] = {
        dataField: obj.dataField,
        highlight: obj.highlight,
      };
    });
  } else if (meta && typeof meta === 'object' && meta.label) {
    newMeta[meta.label] = {
      dataField: meta.dataField,
      highlight: meta.highlight,
    };
  } else if (meta && typeof meta === 'string') {
    return getMetaDataFields(JSON.parse(meta));
  } else if (meta && typeof meta === 'object') return meta;
 
  return newMeta;
};

// Transform the hits returned by the result component
// Use the resultSettings corresponding to the categoryType selected and pass them along with the hits.
export const transformResultData = (
  data: GenericObject[],
  resultSettings: object,
  isPreview: boolean
) => {
  let transformedData: object[] = [];
  (data || []).forEach((item: GenericObject) => {
    const doc_type = get(resultSettings, 'categoryField', '');
    const categoryField = item[doc_type.split('.keyword')[0]];
    const displayFields = get(resultSettings, 'displayFields', '');
    const categoryFieldValues = get(resultSettings, 'categoryFieldValue', []);
    if (categoryFieldValues.length) {
      if (categoryFieldValues.includes(categoryField)) {
        const resultSettingsObj = displayFields[categoryField];

        const handleURL = isPreview
          ? ''
          : get(item, getDataFieldValue(get(resultSettingsObj, 'handle')));
        const image = get(
          item,
          getDataFieldValue(get(resultSettingsObj, 'image'))
        );
        const title = get(
          item,
          getDataFieldValue(get(resultSettingsObj, 'title'))
        );
        const description = get(
          item,
          getDataFieldValue(get(resultSettingsObj, 'description'))
        );
        const price = get(
          item,
          getDataFieldValue(get(resultSettingsObj, 'price'))
        );
        const priceUnit = get(resultSettingsObj, 'priceUnit');
        const handleViewer = get(resultSettingsObj, 'handleViewer');
        const cssSelector = get(resultSettingsObj, 'cssSelector');

        transformedData.push({
          title,
          description,
          image,
          handleURL,
          handleViewer,
          price,
          priceUnit,
          categoryField,
          cssSelector,
          userDefinedFields: getMetaDataFields(
            get(resultSettingsObj, 'userDefinedFields')
          ),
          _source: item,
        });
      } else if (
        Array.isArray(categoryField) &&
        commonElementsInArray(categoryField, categoryFieldValues)
      ) {
        categoryFieldValues.some((field: string) => {
          if (categoryField.includes(field)) {
            const resultSettingsObj = displayFields[field];
            const handleURL = isPreview
              ? ''
              : get(item, getDataFieldValue(get(resultSettingsObj, 'handle')));
            const image = get(
              item,
              getDataFieldValue(get(resultSettingsObj, 'image'))
            );
            const title = get(
              item,
              getDataFieldValue(get(resultSettingsObj, 'title'))
            );
            const description = get(
              item,
              getDataFieldValue(get(resultSettingsObj, 'description'))
            );
            const price = get(
              item,
              getDataFieldValue(get(resultSettingsObj, 'price'))
            );
            const priceUnit = get(resultSettingsObj, 'priceUnit');
            const handleViewer = get(resultSettingsObj, 'handleViewer');
            const cssSelector = get(resultSettingsObj, 'cssSelector');

            transformedData.push({
              title,
              description,
              image,
              handleURL,
              handleViewer,
              price,
              priceUnit,
              categoryField,
              cssSelector,
              userDefinedFields: getMetaDataFields(
                get(resultSettingsObj, 'userDefinedFields')
              ),
              _source: item,
            });

            return true;
          }

          return false;
        });
      } else {
        const handleURL = isPreview
          ? ''
          : get(item, getDataFieldValue(get(displayFields, '_default.handle')));
        const image = get(
          item,
          getDataFieldValue(get(displayFields, '_default.image'))
        );
        const title = get(
          item,
          getDataFieldValue(get(displayFields, '_default.title'))
        );
        const description = get(
          item,
          getDataFieldValue(get(displayFields, '_default.description'))
        );
        const price = get(
          item,
          getDataFieldValue(get(displayFields, '_default.price'))
        );
        const priceUnit = get(displayFields, '_default.priceUnit');
        const handleViewer = get(displayFields, '_default.handleViewer');
        const cssSelector = get(displayFields, '_default.cssSelector');

        transformedData.push({
          title,
          description,
          image,
          handleURL,
          handleViewer,
          price,
          priceUnit,
          categoryField,
          cssSelector,
          userDefinedFields: getMetaDataFields(
            get(displayFields, '_default.userDefinedFields')
          ),
          _source: item,
        });
      }
    } else {
      const handleURL = isPreview
        ? ''
        : get(item, getDataFieldValue(get(resultSettings, 'fields.handle')));

      const image = get(
        item,
        getDataFieldValue(get(resultSettings, 'fields.image'))
      );
      const title = get(
        item,
        getDataFieldValue(get(resultSettings, 'fields.title'))
      );

      const description = get(
        item,
        getDataFieldValue(get(resultSettings, 'fields.description'))
      );
      const price = get(
        item,
        getDataFieldValue(get(resultSettings, 'fields.price'))
      );
      const priceUnit = get(resultSettings, 'fields.priceUnit');
      const handleViewer = get(resultSettings, 'fields.handleViewer');
      const cssSelector = get(resultSettings, 'fields.cssSelector');

      transformedData.push({
        title,
        description,
        image,
        handleURL,
        handleViewer,
        price,
        priceUnit,
        categoryField,
        cssSelector,
        userDefinedFields: getMetaDataFields(
          get(resultSettings, 'fields.userDefinedFields')
        ),
        _source: item,
      });
    }
  });

  return transformedData;
};
